// Enhanced Axios instance for Multi-Franchise operations
// Independent axios instance with franchise context support

import axios from 'axios';
import { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { store } from '../../store';
import { ESliceAuth, setAccessToken } from '../../store/slices/authSlice';
import {
    isAdminSessionExpiredError,
    notifyAdminSessionExpired,
} from '../auth/adminSessionEvents';
import {
  resolveRefreshedAdminSessionTokens,
  shouldAttemptAdminSessionRefresh,
  shouldNotifyAdminSessionExpiredAfterRefreshFailure,
  shouldNotifyAdminSessionExpired,
} from '../auth/adminSessionRecovery';
import { requestNewAccessToken } from '.';
import { resolveFranchiseContextUuid } from './franchiseContext';

// Create franchise-aware axios instance
export const multiFranchiseAxios: AxiosInstance = axios.create({
  // Use relative path in development to leverage Vite proxy
  baseURL: import.meta.env.VITE_IS_DEV === 'true' 
    ? '' // Empty baseURL for relative paths (proxied by Vite)
    : (import.meta.env.VITE_FARMBOOK_API),
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add franchise context to headers
multiFranchiseAxios.interceptors.request.use(
  (config: AxiosRequestConfig) => {
    // Get authorization token from Redux store
    const storeState = store.getState();
    const AuthInfo = storeState[ESliceAuth.AuthInfo];

    if (AuthInfo.access_token && config.headers) {
      config.headers['Authorization'] = `Bearer ${AuthInfo.access_token}`;
    }

    // Company identity is not a franchise. Accounting routes validate the
    // stored selection against the current actor's available franchises.
    const currentFranchiseUuid = localStorage.getItem('currentFranchiseUuid');
    const requestedFranchiseUuid = config.headers &&
      typeof config.headers['X-Franchise-Context'] === 'string'
      ? config.headers['X-Franchise-Context']
      : '';
    const fallbackFranchiseUuid = resolveFranchiseContextUuid(
      requestedFranchiseUuid,
      currentFranchiseUuid
    );

    if (fallbackFranchiseUuid && config.headers) {
      config.headers['X-Franchise-Context'] = fallbackFranchiseUuid;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

interface RetryableAxiosRequestConfig extends AxiosRequestConfig {
  _retry?: boolean;
}

const attachAdminSessionExpiryInterceptor = (instance: AxiosInstance): AxiosInstance => {
  instance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError): Promise<AxiosResponse> => {
      const storeState = store.getState();
      const AuthInfo = storeState[ESliceAuth.AuthInfo];
      const originalRequest = error.config as RetryableAxiosRequestConfig | undefined;

      if (shouldAttemptAdminSessionRefresh(error) && AuthInfo.refresh_token && originalRequest) {
        originalRequest._retry = true;
        try {
          const { access_token, refresh_token } = await requestNewAccessToken(AuthInfo.refresh_token);
          const refreshedTokens = resolveRefreshedAdminSessionTokens(
            { access_token, refresh_token },
            AuthInfo.refresh_token
          );
          if (refreshedTokens) {
            store.dispatch(setAccessToken(refreshedTokens));
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers['Authorization'] = `Bearer ${refreshedTokens.access_token}`;
            return instance(originalRequest);
          }
          notifyAdminSessionExpired();
        } catch {
          if (shouldNotifyAdminSessionExpiredAfterRefreshFailure(error)) {
            notifyAdminSessionExpired();
          }
        }
      } else if (
        isAdminSessionExpiredError(error) ||
        shouldNotifyAdminSessionExpired(error, Boolean(AuthInfo.refresh_token))
      ) {
        notifyAdminSessionExpired();
      }
      return Promise.reject(error);
    },
  );
  return instance;
};

attachAdminSessionExpiryInterceptor(multiFranchiseAxios);

// Helper function to create API client with specific franchise context
export const createFranchiseAwareAPIClient = (franchiseUuid?: string) => {
  const instance = axios.create({
    // Use relative path in development to leverage Vite proxy
    baseURL: import.meta.env.VITE_IS_DEV === 'true' 
      ? '' // Empty baseURL for relative paths (proxied by Vite)
      : (import.meta.env.VITE_FARMBOOK_API),
    timeout: 30000,
    withCredentials: true
  });
  
  // Copy existing interceptors
  instance.interceptors.request.use((config: AxiosRequestConfig) => {
    // Get authorization token from Redux store
    const storeState = store.getState();
    const AuthInfo = storeState[ESliceAuth.AuthInfo];
    
    if (AuthInfo.access_token && config.headers) {
      config.headers['Authorization'] = `Bearer ${AuthInfo.access_token}`;
    }
    
    const context = resolveFranchiseContextUuid(
      franchiseUuid,
      localStorage.getItem('currentFranchiseUuid')
    );
    if (context && config.headers) {
      config.headers['X-Franchise-Context'] = context;
    }
    return config;
  });

  return attachAdminSessionExpiryInterceptor(instance);
};

// Export the main instance for multi-franchise operations
export default multiFranchiseAxios;
